/**
 * StageScreen / ScreenHead — the presentational frame every L1A stage screen
 * reuses. Maps 1:1 to the mockup `.screen` + `.screen-head` (title + status
 * pill + sub-caption) with a body slot. No business logic — the frame owns the
 * heading/pill; the existing element drops in as `children`.
 *
 * One flat scroll, AVBP green, namespaced under `.l1a-screen` so the mockup's
 * generic class names never collide with the shared `.card`/`.row-card`
 * primitives. `id="stage-panel"` matches `StageTabBar`'s `aria-controls`.
 */
import type { ReactNode } from 'react';
import type { StageStatus } from '../../lib/l1aTabs';

const STATUS_WORD: Record<StageStatus, string> = {
  done: 'done',
  now: 'in progress',
  todo: 'not started',
};

export function ScreenHead({
  title,
  status,
  statusLine,
  sub,
}: {
  title: string;
  /** Omit for meta tabs (Overview/Settings) — renders no pill. */
  status?: StageStatus;
  /** Optional explicit pill copy; defaults to the status word. */
  statusLine?: string;
  sub?: string;
}) {
  return (
    <div className="screen-head">
      <h1>{title}</h1>
      {status && <span className={`st ${status}`}>{statusLine ?? STATUS_WORD[status]}</span>}
      {sub && <span className="sub">{sub}</span>}
    </div>
  );
}

export default function StageScreen({
  title,
  status,
  statusLine,
  sub,
  tabKey,
  testId,
  children,
}: {
  title: string;
  status?: StageStatus;
  statusLine?: string;
  sub?: string;
  /** The active tab key — wires aria-labelledby back to its bar tab. */
  tabKey?: string;
  testId?: string;
  children?: ReactNode;
}) {
  return (
    <section
      className="l1a-screen"
      role="tabpanel"
      id="stage-panel"
      aria-labelledby={tabKey ? `tab-${tabKey}` : undefined}
      tabIndex={0}
      data-testid={testId}
    >
      <ScreenHead title={title} status={status} statusLine={statusLine} sub={sub} />
      {children}
    </section>
  );
}
